import React, { useState, useEffect } from 'react';
import { Calculator, Users, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const NetPromoterScoreCalculator = () => {
  const [promoters, setPromoters] = useState(60);
  const [passives, setPassives] = useState(30);
  const [detractors, setDetractors] = useState(10);
  const [npsScore, setNpsScore] = useState(0);

  useEffect(() => {
    calculateNPS();
  }, [promoters, passives, detractors]);

  const calculateNPS = () => {
    const totalResponses = promoters + passives + detractors;
    const nps = ((promoters - detractors) / totalResponses) * 100;
    setNpsScore(Math.round(nps));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/net-promoter-score-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>NPS Calculator: Measure Net Promoter Score | Free Tool</title>
        <meta name="description" content="Calculate your Net Promoter Score (NPS) instantly. Compare with industry benchmarks, get optimization tips, and improve customer loyalty." />
        <meta name="keywords" content="NPS calculator, net promoter score, customer loyalty metrics, promoter score calculator, customer experience analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free NPS Calculator: Boost Customer Loyalty" />
        <meta property="og:description" content="Calculate your Net Promoter Score instantly. Get actionable insights to improve customer loyalty and advocacy." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free NPS Calculator: Boost Customer Loyalty" />
        <meta property="twitter:description" content="Calculate your Net Promoter Score instantly. Get actionable insights to improve customer loyalty and advocacy." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Net Promoter Score Calculator",
            "description": "Free calculator to measure and improve Net Promoter Score (NPS)",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate NPS Score",
              "Industry Benchmarks",
              "Loyalty Improvement Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Net Promoter Score",
              "description": "A metric that measures customer loyalty and likelihood to recommend based on responses to a standardized question."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Customer Experience Managers, Business Owners, Marketing Professionals"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Net Promoter Score",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of promoters (9-10 ratings)"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of passives (7-8 ratings)"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add number of detractors (0-6 ratings)"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your NPS and improvement strategies"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Net Promoter Score (NPS) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Promoters (9-10)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ThumbsUp className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={promoters}
                  onChange={(e) => setPromoters(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="60"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Passives (7-8)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={passives}
                  onChange={(e) => setPassives(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="30"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Detractors (0-6)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ThumbsDown className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={detractors}
                  onChange={(e) => setDetractors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2">
            {npsScore}
          </div>
          <p className="text-gray-600">Net Promoter Score (NPS)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Net Promoter Score (NPS) is {npsScore}. NPS ranges from -100 to +100, measuring customer loyalty and likelihood to recommend.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Score Classification</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>{'<'} 0: Needs Improvement</li>
          <li>0-30: Good</li>
          <li>30-70: Great</li>
          <li>{'>'} 70: Excellent</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Technology: 35-45</li>
          <li>Retail: 30-40</li>
          <li>Financial Services: 25-35</li>
          <li>Healthcare: 30-40</li>
          <li>E-commerce: 35-45</li>
          <li>B2B Services: 40-50</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">NPS Improvement Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Follow up with detractors promptly</li>
          <li>Analyze passive feedback for opportunities</li>
          <li>Leverage promoters for advocacy</li>
          <li>Improve based on feedback</li>
          <li>Train team for better experiences</li>
          <li>Implement closed-loop feedback</li>
          <li>Conduct regular NPS surveys</li>
          <li>Create action plans for improvement</li>
          <li>Monitor trends over time</li>
          <li>Benchmark against competitors</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Business Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Promoters have 3x lifetime value vs detractors</li>
          <li>Promoters are 5x more likely to repurchase</li>
          <li>Promoters refer 3-4 new customers on average</li>
          <li>7-point NPS increase = 1% revenue growth</li>
          <li>Detractors account for 80% of negative reviews</li>
        </ul>
      </div>
    </div>
  );
};

export default NetPromoterScoreCalculator;
